import { getFormUrl } from "@/utils/form";
import { useState } from "react";
import ScrollReveal from "@/components/base/ScrollReveal";

export default function Contact() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const hp = formData.get("contact_alt")?.toString().trim();
    if (hp) {
      setFormStatus("success");
      form.reset();
      return;
    }

    try {
      const submitAddr = getFormUrl("contato-bigmania");
      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as any).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError(serverMsg || "Erro ao enviar. Tente novamente.");
      }
    } catch {
      setFormStatus("error");
      setFormError("Erro de conexao. Verifique sua internet e tente novamente.");
    }
  };

  return (
    <section id="contato" className="relative w-full py-20 md:py-28 bg-organic-cool overflow-hidden">
      <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
        <ScrollReveal className="text-center mb-14">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600 mb-4">
            Entre em Contato
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
            Fale com a{" "}
            <span className="text-primary-600">Big Mania</span>
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto text-base md:text-lg">
            Estamos prontos para atender você. Entre em contato para pedidos, orçamentos ou parcerias.
          </p>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12">
          {/* Contact Info */}
          <ScrollReveal direction="left" delay={0.1}>
            <div className="space-y-6">
              <div className="p-6 rounded-2xl bg-background-50/80 border border-background-200/40 card-glow">
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-4">Informacoes</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">Endereco</p>
                      <p className="text-sm text-foreground-600">Rua Capitao Vasconcelos, 233</p>
                      <p className="text-sm text-foreground-600">Aerolandia, Fortaleza - CE</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">Horario de Funcionamento</p>
                      <p className="text-sm text-foreground-600">Segunda a Sexta: 07h as 17h</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">Telefone</p>
                      <p className="text-sm text-foreground-600">(85) 3494-2701</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-whatsapp-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">WhatsApp</p>
                      <p className="text-sm text-foreground-600">(85) 3494-2701</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <a
                  href="tel:+558534942701"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50/80 border border-background-200/40 card-glow transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                >
                  <i className="ri-phone-fill text-2xl text-primary-500"></i>
                  <span className="text-xs font-medium text-foreground-800 text-center">Ligar</span>
                </a>
                <a
                  href="https://wa.me/558534942701"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50/80 border border-background-200/40 card-glow transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-2xl text-[#25D366]"></i>
                  <span className="text-xs font-medium text-foreground-800 text-center">WhatsApp</span>
                </a>
                <a
                  href="https://maps.google.com/?q=-3.7327,-38.5270"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50/80 border border-background-200/40 card-glow transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                >
                  <i className="ri-map-pin-line text-2xl text-secondary-500"></i>
                  <span className="text-xs font-medium text-foreground-800 text-center">Localizacao</span>
                </a>
              </div>
            </div>
          </ScrollReveal>

          {/* Contact Form */}
          <ScrollReveal direction="right" delay={0.2}>
            <div className="p-6 md:p-8 rounded-2xl bg-background-50/80 border border-background-200/40 card-glow">
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-6">
                Envie uma mensagem
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input type="text" name="contact_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nome</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-colors"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">Telefone</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-colors"
                      placeholder="(85) 99999-9999"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-colors"
                    placeholder="seu@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Assunto</label>
                  <select
                    name="subject"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-colors appearance-none"
                  >
                    <option value="">Selecione um assunto</option>
                    <option value="pedido">Pedido de Produtos</option>
                    <option value="orcamento">Orçamento para Evento</option>
                    <option value="revenda">Quero ser Revendedor</option>
                    <option value="empresa">Atendimento para Empresa</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Mensagem</label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200/70 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-colors resize-none"
                    placeholder="Conte-nos como podemos ajudar..."
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">Maximo 500 caracteres</p>
                </div>

                {formStatus === "success" && (
                  <div className="p-4 rounded-lg bg-accent-100 border border-accent-200 text-accent-800 text-sm">
                    <i className="ri-check-line mr-1"></i>
                    Mensagem enviada com sucesso! Entraremos em contato em breve.
                  </div>
                )}
                {formStatus === "error" && (
                  <div className="p-4 rounded-lg bg-secondary-100 border border-secondary-200 text-secondary-800 text-sm">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError || "Erro ao enviar. Tente novamente."}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {formStatus === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Enviar Mensagem
                    </>
                  )}
                </button>
              </form>
            </div>
          </ScrollReveal>
        </div>

        {/* Map */}
        <ScrollReveal direction="up" delay={0.3}>
          <div className="mt-12 rounded-2xl overflow-hidden border border-background-200/40 h-72 sm:h-96 card-glow">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3981.3118!2d-38.5270!3d-3.7327!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM8KwNDMnNTcuNyJTIDM4wrAzMSczNy4yIlc!5e0!3m2!1spt-BR!2sbr!4v1"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localizacao Big Mania"
              className="w-full h-full"
            ></iframe>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}